import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { Send } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { FormField, Input } from '@/components/ui/FormField';
import { Alert } from '@/components/ui/Alert';
import { useMarkSubmitted, type Claim } from '@/api/claims';

// Mark-as-Submitted flow for Manual-payer Ready claims. The server
// requires every row in the selection to be Payer + Manual + Ready;
// a 409 comes back with a `detail` pointing at the first offender.
// We surface that directly so the biller knows which claim to pull.

interface Props {
    claims: Claim[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess?: () => void;
}

function todayIso(): string {
    // The server expects YYYY-MM-DD local-date. Using toISOString() would
    // shift the day around midnight in non-UTC zones, so format from the
    // local pieces instead.
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

export function MarkSubmittedDialog({ claims, open, onOpenChange, onSuccess }: Props) {
    const mutation = useMarkSubmitted();
    const [submitDate, setSubmitDate] = useState<string>(todayIso());
    const [confirmation, setConfirmation] = useState('');

    useEffect(() => {
        if (open) {
            setSubmitDate(todayIso());
            setConfirmation('');
            mutation.reset();
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const count = claims.length;
    const dateInvalid = !/^\d{4}-\d{2}-\d{2}$/.test(submitDate);
    const confirmationInvalid = confirmation.length > 64;

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (count === 0 || dateInvalid || confirmationInvalid) return;
        try {
            await mutation.mutateAsync({
                ids: claims.map((c) => c.id),
                submitDate,
                confirmation: confirmation.trim() || undefined,
            });
            onSuccess?.();
            onOpenChange(false);
        } catch {
            /* surfaced below */
        }
    }

    const serverError = (() => {
        const e = mutation.error;
        if (!e) return null;
        if (e instanceof AxiosError) {
            const data = e.response?.data as { error?: string; detail?: Record<string, unknown> } | undefined;
            if (data?.detail && typeof data.detail === 'object') {
                const d = data.detail as {
                    display_id?: string;
                    billTo?: string;
                    submissionMethod?: string;
                    status?: string;
                };
                const reasons: string[] = [];
                if (d.billTo && d.billTo !== 'Payer') reasons.push(`bill to ${d.billTo}`);
                if (d.submissionMethod && d.submissionMethod !== 'Manual') {
                    reasons.push(d.submissionMethod === 'Electronic' ? 'Electronic payer' : `submission ${d.submissionMethod}`);
                }
                if (d.status && d.status !== 'Ready') reasons.push(`status ${d.status}`);
                const reasonText = reasons.length ? ` — ${reasons.join(', ')}` : '';
                return `${data.error ?? 'Selection rejected'}: ${d.display_id ?? 'claim'}${reasonText}`;
            }
            return data?.error ?? 'Failed to mark claims submitted';
        }
        return 'Unexpected error';
    })();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[min(calc(100vw-2rem),32rem)]">
                <DialogHeader>
                    <DialogTitle>
                        Mark {count} claim{count === 1 ? '' : 's'} as submitted?
                    </DialogTitle>
                    <DialogDescription>
                        Record that you sent these claims outside the app (paper, payer portal,
                        fax). Status flips to Submitted and the aging clock starts from the
                        submit date — no EDI upload happens.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={onSubmit} noValidate>
                    {serverError && <Alert variant="danger" className="mb-4">{serverError}</Alert>}

                    <div className="grid grid-cols-2 gap-x-4">
                        <FormField
                            label="Submit date"
                            required
                            hint="Sets the aging-bucket start date."
                        >
                            {(id) => (
                                <Input
                                    id={id}
                                    type="date"
                                    value={submitDate}
                                    max={todayIso()}
                                    onChange={(e) => setSubmitDate(e.target.value)}
                                />
                            )}
                        </FormField>

                        <FormField
                            label="Confirmation #"
                            hint="Optional. Portal case #, fax confirmation, etc."
                            error={confirmationInvalid ? 'Max 64 characters' : undefined}
                        >
                            {(id) => (
                                <Input
                                    id={id}
                                    value={confirmation}
                                    onChange={(e) => setConfirmation(e.target.value)}
                                    maxLength={80}
                                    placeholder="e.g., AV-49201"
                                />
                            )}
                        </FormField>
                    </div>

                    {count > 0 && (
                        <div className="mt-3 max-h-32 overflow-y-auto border border-border rounded-md bg-surface-muted p-2 text-xs text-text-muted">
                            {claims.slice(0, 8).map((c) => (
                                <div key={c.id} className="flex items-center justify-between py-0.5">
                                    <span className="font-medium text-text">{c.claimNo}</span>
                                    <span className="truncate pl-3">{c.client}</span>
                                </div>
                            ))}
                            {count > 8 && (
                                <div className="pt-1 text-text-subtle">…and {count - 8} more</div>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            disabled={mutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={mutation.isPending || count === 0 || dateInvalid || confirmationInvalid}
                        >
                            <Send className="w-3.5 h-3.5" />
                            {mutation.isPending ? 'Saving…' : `Mark ${count} submitted`}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
